import { Box, CloseButton, Flex, Icon, Text, useToken } from "@chakra-ui/react";
import { PropsWithChildren, ReactNode, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { LngLatLike, Popup, PopupProps } from "react-map-gl/maplibre";

import { LocationIcon, RanaHeading } from "@nens/ranalib";
import { useRect } from "@nens/react/hooks";
import { normalizeLngLat } from "@nens/react-map-gl/utils";

import { PopupRasterInfo } from "./components/raster-popup-info";
import { PopupScenarioInfo } from "./components/scenario-popup-info";
import { VectorInfo } from "./components/vector-popup-info";
import { useMapCenterLock } from "./hooks/use-map-center-lock";

export const RANA_POPUP_PADDING_PX = 16 as const;
const POPUP_TOP_MARGIN_PX = 16; // Minimum gap between the popup top edge and the viewport top (this can be change)

interface RanaPopupProps extends PropsWithChildren {
  point: LngLatLike | null;
  onClose: () => void;
  anchor?: PopupProps["anchor"];
  offset?: PopupProps["offset"];
  header?: ReactNode; // Pass `null` to hide the header entirely. Omit to render the default location header
  hideCloseButton?: boolean;
}

const useVerticalOffsetPx = (offset: number) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { height: popupHeight } = useRect(contentRef);

  // If the popup is taller than the space above the viewport center, move the
  // anchor point down so that the popup can grow upwards without going offscreen.
  // Also add some extra space (POPUP_TOP_MARGIN_PX) to ensure the popup doesn't touch the top edge of the screen,
  // and POPUP_PADDING_Y_PX to account for the popup's internal padding.
  const verticalOffsetPx = Math.max(
    0,
    popupHeight - window.innerHeight / 2 + POPUP_TOP_MARGIN_PX + offset + RANA_POPUP_PADDING_PX * 2
  );
  return { contentRef, verticalOffsetPx };
};

export function RanaPopup({
  anchor,
  offset,
  header,
  point,
  onClose,
  hideCloseButton,
  children,
}: RanaPopupProps) {
  const [docked] = useToken("zIndices", ["docked"]);
  const { contentRef, verticalOffsetPx } = useVerticalOffsetPx(
    typeof offset === "number" ? offset : 0
  );

  useMapCenterLock(point, verticalOffsetPx);

  // Users can close the RanaPopup by using the escape button
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!point) return null;

  // Normalize the point to LngLat for consistent access to lng/lat properties
  const normalizedPoint = normalizeLngLat(point);

  return (
    <Popup
      maxWidth="xl"
      style={{
        zIndex: docked,
      }}
      anchor={anchor}
      offset={offset}
      longitude={normalizedPoint.lng}
      latitude={normalizedPoint.lat}
      closeButton={false}
      onClose={onClose}
    >
      <Flex
        ref={contentRef}
        gap={3}
        rounded="base"
        justifyContent="center"
        flexDirection="column"
        color="brand.deep_sea"
      >
        {header === null ? (
          <Flex pb={2} />
        ) : (
          <Flex
            flexDir="column"
            borderBottomWidth="base"
            borderColor="brand.border"
            minW={44}
            fontWeight={300}
            pb={2}
          >
            {header ?? (
              <>
                <Flex alignItems="flex-start">
                  <Icon as={LocationIcon} boxSize={6} color="brand.sky" />
                  <RanaHeading type="h6">
                    <FormattedMessage id="popup.location" defaultMessage="Location" />
                  </RanaHeading>
                </Flex>
                <Text
                  textStyle="xs"
                  whiteSpace="nowrap"
                  color="brand.clear_creek"
                  fontWeight={700}
                  ml={6}
                >
                  {normalizedPoint.lng.toFixed(5)}, {normalizedPoint.lat.toFixed(5)}
                </Text>
              </>
            )}
          </Flex>
        )}
        <Box textStyle="sm">{children}</Box>
        {!hideCloseButton && (
          <CloseButton
            size="sm"
            position="absolute"
            right={2}
            top={2}
            onClick={onClose}
            color="brand.clear_creek"
          />
        )}
      </Flex>
    </Popup>
  );
}

RanaPopup.RasterInfo = PopupRasterInfo;
RanaPopup.ScenarioInfo = PopupScenarioInfo;
RanaPopup.VectorInfo = VectorInfo;
